import React, { useContext, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientAreaLog } from 'api/ClientArea/ClientAreaLog'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { ReactDatePicker } from 'components/DatePicker/DatePicker'
import { Loading } from 'components/Loading'
import { buildSearch, Expression, Operator, SearchQuery } from 'helpers/generic/search'
import omitEmpty from 'omit-empty'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { TableWithExport } from 'utils/table/withExport'
import { zonedTimeToUtc } from 'utils/time-zone/helpers'

interface Props {
  clientId: string
}

interface Values {
  group?: string
  from?: Date | null
  to?: Date | null
}

const createDataState = (state: State, data: ClientAreaLog[], total: number) => ({
  result: data,
  dataState: state,
  total,
})

const ClientAreaAccessLogsModal = ({ clientId }: Props): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false)
  const [form, setForm] = useState<Values>({})
  const [clientAreaTypes, setClientAreaTypes] = useState<string[]>([])
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const [searchQuery, setSearchQuery] = useState<SearchQuery[]>([])

  const [paginationData, setPaginationData] = useState<PageData<ClientAreaLog[]> | null>(null)
  const [dataState, setDataState] = useState<{
    result: ClientAreaLog[]
    dataState: State
    total: number
  }>(
    createDataState(
      getPaginationInitialState(),
      (paginationData && paginationData.items) || [],
      (paginationData && paginationData.totalResults) || 0
    )
  )

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    if (paginationData)
      setDataState(
        createDataState(
          event.dataState,
          paginationData.items as ClientAreaLog[],
          paginationData.totalResults
        )
      )
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await clientAreaApiClient.getClientAreaGroups()
      const groups = response.map((group) => group.name)
      setClientAreaTypes(groups)
      return setLoading(false)
    } catch (error) {
      console.error(error)
      return setLoading(false)
    }
  }

  const initiateSearch = () => {
    const searchQueryString: SearchQuery[] = []

    const from = form.from ? zonedTimeToUtc(form.from).toISOString() : null
    const to = form.to ? zonedTimeToUtc(form.to).toISOString() : null

    if (from)
      searchQueryString.push({ field: 'Date', value: from, expression: Expression.moreThanEquals })
    if (to)
      searchQueryString.push({ field: 'Date', value: to, expression: Expression.lessThanEquals })
    if (form.group && form.group !== 'All')
      searchQueryString.push({ field: 'Action', value: form.group, expression: Expression.equals })
    searchQueryString.push({ field: 'UserId', value: clientId, expression: Expression.equals })

    setSearchQuery(searchQueryString)
    return searchQueryString
  }

  const handleSearch = async (initialSearchQuery?: SearchQuery[]) => {
    setLoading(true)
    const parsedSearchQuery = buildSearch({
      searchParams: initialSearchQuery?.length ? initialSearchQuery : searchQuery,
      operator: Operator.AND,
    })
    try {
      const response = await clientAreaApiClient.getClientAreaLogs(
        omitEmpty({
          search: parsedSearchQuery,
          pageIndex: 1,
          pageSize: 10,
        })
      )
      setPaginationData(response)
      setDataState(createDataState({ skip: 0 }, response.items, response.totalResults))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    const initialSearchQuery = initiateSearch()
    handleSearch(initialSearchQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    initiateSearch()
  }, [clientId, form.from, form.to, form.group]) // eslint-disable-line react-hooks/exhaustive-deps

  const pageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page

    const pagination = new Pagination({ skip, recordsPerPage: take })

    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())

    setLoading(true)
    const parsedSearchQuery = buildSearch({ searchParams: searchQuery, operator: Operator.AND })
    try {
      const response = await clientAreaApiClient.getClientAreaLogs(
        omitEmpty({
          search: parsedSearchQuery,
          pageIndex: pagination.getPageIndex() > 0 ? pagination.getPageIndex() : 1,
          pageSize: take,
        })
      )
      setDataState(createDataState({ take, skip }, response.items, response.totalResults))
      setLoading(false)
    } catch {
      setLoading(false)
    }
  }

  return (
    <DialogContent className='flex h-[80vh] max-w-275 flex-col'>
      <DialogHeader>
        <DialogTitle>Client Area Access Logs</DialogTitle>
      </DialogHeader>

      <Loading isLoading={loading} modalView className='flex min-h-0 flex-1 flex-col'>
        <div className='flex min-h-0 flex-col gap-4'>
          <div className='grid gap-4 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-end'>
            <div className='space-y-2'>
              <label className='text-sm font-medium'>Action</label>
              <select
                value={form.group ?? 'All'}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                  setForm({ ...form, group: event.target.value })
                }}
                autoComplete='off'
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs outline-none'
              >
                <option value='All'>All</option>
                {(clientAreaTypes || []).map((type: string) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className='space-y-2'>
              <label className='text-sm font-medium'>Period</label>
              <div className='flex items-center gap-3'>
                <ReactDatePicker
                  showYearDropdown
                  dropdownMode='select'
                  isClearable
                  selected={form.from ? new Date(form.from) : null}
                  onChange={(value) => {
                    setForm({ ...form, from: value })
                  }}
                  customInput={<Input className='w-full' type='text' autoComplete='off' />}
                />
                <span className='text-sm text-muted-foreground'>to</span>
                <ReactDatePicker
                  showYearDropdown
                  dropdownMode='select'
                  isClearable
                  selected={form.to ? new Date(form.to) : null}
                  onChange={(value) => {
                    setForm({ ...form, to: value })
                  }}
                  customInput={<Input className='w-full' type='text' autoComplete='off' />}
                />
              </div>
            </div>

            <Button type='button' onClick={() => handleSearch()}>
              Search
            </Button>
          </div>

          <div className='min-h-0 flex-1 overflow-auto'>
            <TableWithExport
              data={dataState.result}
              hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportAccessLog)}
            >
              <Grid
                resizable
                take={dataState.dataState.take}
                skip={dataState.dataState.skip}
                onDataStateChange={dataStateChange}
                total={dataState.total}
                data={dataState.result}
                onPageChange={pageChange}
                sortable
                pageable={pageableSettings}
                style={{ zIndex: 0 }}
              >
                <Column className='table-columns__content' field='action' title='Group' />
                <Column className='table-columns__content' field='path' title='Action' />
                <Column
                  title='IP Address'
                  field='ipAddress'
                  cell={(gridCellProps: GridCellProps) => (
                    <td
                      className='table-columns__link'
                      onClick={() =>
                        window.open(
                          `https://whatismyipaddress.com/ip/${gridCellProps.dataItem.ipAddress}`
                        )
                      }
                    >
                      {gridCellProps.dataItem.ipAddress}
                    </td>
                  )}
                />
                <Column className='table-columns__content' field='userAgent' title='User-Agent' />
                <Column className='table-columns__content' field='sessionId' title='Session ID' />
                <Column
                  className='table-columns__content'
                  field='callDuration'
                  title='Call Duration'
                />
                <Column
                  field='date'
                  title='Created'
                  cell={(gridCellProps: GridCellProps) => (
                    <td>{formatLocalDateUTC(gridCellProps.dataItem.date, true)}</td>
                  )}
                />
              </Grid>
            </TableWithExport>
          </div>
        </div>
      </Loading>
    </DialogContent>
  )
}

export default ClientAreaAccessLogsModal
